/**
 * WP3 — "your details", straight after a fresh signup's first OTP verify.
 * Two fields. `parents.name` is NOT NULL and parent-api defaults an unnamed
 * signup to the literal string 'Parent' (`parent_name || 'Parent'`,
 * auth.ts), so this is where a real name gets collected. No Back — the
 * account already exists by the time this screen shows.
 *
 * The name starts empty. It must NOT copy ParentDetailsSheet's
 * `defaultValues: { parentName, ... }` — at this point `authStore.parentName`
 * IS that literal 'Parent' default, so prefilling would satisfy the
 * required-field rule with junk and turn this into a tap-through no-op.
 *
 * On the invitation path (TUP-1835: the parent came from a child's invite
 * link, so the phone is already verified) the screen asks only for the
 * parent's name — no language picker — and hands on to /invite instead of the
 * plans pitch. Linking never waits on a plan. The device language still seeds
 * the stored UI language below, so `preferred_language` is saved either way.
 *
 * The language picker fills `parents.preferred_language`, a column that
 * existed and was read by nothing. It has no visible effect until parent i18n
 * (WP8) ships, so the label says "App language" and claims nothing more — the
 * point of asking now is that onboarding is the one moment a parent expects
 * to be asked, and WP8 would otherwise have to prompt an installed base.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLayoutEffect, useState } from 'react';
import { IconUser } from '@tabler/icons-react';
import { capture } from '@tupai/analytics/client';
import { useAuthStore } from '@/store/auth-store';
import { updateAccount, type AppLanguage } from '@/lib/api';
import { setLanguage, useLanguage, hasStoredLanguage } from '@/lib/language';
import { useTranslation, type TFunction } from '@tupai/i18n/react';
import { LANGUAGE_LABELS } from '@/components/app-language-sheet';
import { afterDetailsPath } from '@/lib/invite-flow';
import { hasPendingInvite } from '@/lib/pending-invite';

export const Route = createFileRoute('/_protected/onboarding/details')({
  component: OnboardingDetailsPage,
});

// A FACTORY, not a module constant — see PhoneAuthForm for why.
const makeDetailsSchema = (t: TFunction<'auth'>) =>
  z.object({
    parentName: z.string().min(1, t('errNameRequired')),
  });
type DetailsFormValues = z.infer<ReturnType<typeof makeDetailsSchema>>;

// Labels come from AppLanguageSheet so this picker and Profile's can't drift,
// and each option stays labelled in its own language.
const LANGUAGES = Object.keys(LANGUAGE_LABELS) as AppLanguage[];

/** Prefix match, not equality — a Malay device reports 'ms-MY', not 'ms'. */
function deviceLanguage(): AppLanguage {
  return navigator.language.toLowerCase().startsWith('ms') ? 'ms' : 'en';
}

// The device's language is a first guess for a parent who has never chosen
// one, so the picker opens pre-set to it. Only when nothing is stored — a
// returning parent's own choice is never overwritten by their handset's locale.
function seedFromDevice(): void {
  if (!hasStoredLanguage()) setLanguage(deviceLanguage());
}

function OnboardingDetailsPage() {
  const navigate = useNavigate();
  const { setParentName, setStoredName } = useAuthStore();
  // The picker writes straight to the language store rather than to local form
  // state, so the screen itself switches under the parent's finger — the choice
  // is worth nothing if they can't see what it did.
  //
  // The seed runs in a LAYOUT effect, not during render: setLanguage notifies
  // every useSyncExternalStore subscriber, and doing that mid-render is a
  // setState-during-render of another component. Layout timing still lands it
  // before paint, so a Malay handset never flashes English. StrictMode's second
  // invoke is a no-op — by then something is stored.
  useLayoutEffect(seedFromDevice, []);
  const language = useLanguage();
  const { t } = useTranslation('auth');
  // Read once: the invite is cleared only on /invite, but a re-render must
  // not flip the form halfway through typing.
  const [onInvitePath] = useState(hasPendingInvite);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<DetailsFormValues>({
    resolver: zodResolver(makeDetailsSchema(t)),
    defaultValues: { parentName: '' },
  });

  const nameEntered = watch('parentName').trim().length > 0;

  const onSave = handleSubmit(async (data) => {
    try {
      const updated = await updateAccount({
        parentName: data.parentName.trim(),
        preferredLanguage: language,
      });
      setParentName(updated.parent_name);
      setStoredName(updated.parent_name);
      navigate({ to: afterDetailsPath(), replace: true });
    } catch {
      setError('root', { message: t('errSaveDetails') });
    }
  });

  return (
    // Page canvas stays `surface` (DESIGN.md: cream `bg` is never a page
    // background) with the identity art on a primary tint stage — the same
    // device the plans page uses, so the onboarding screens read as one
    // flow rather than a run of blank forms.
    <div className="flex min-h-0 flex-1 flex-col bg-surface">
      <form onSubmit={onSave} className="flex flex-1 flex-col overflow-y-auto pb-6">
        {/* `.safe-area-top` sets padding-top from unlayered CSS and beats any
            Tailwind `pt-*` on the same element, so the inset lives on its own
            spacer and the stage keeps its own padding. */}
        <div className="safe-area-top shrink-0" />
        <div className="flex flex-1 flex-col md:flex-none md:my-auto">
          <div className="bg-primary/10 px-6 pb-7 pt-8">
            {/* Icon-in-a-disc rather than the parent spot art: that asset carries
                its own magenta backing square, which fights the tint. Same recipe
                as PaywallPage's step-1 hero, inverted for the tinted stage. */}
            <div className="mx-auto flex size-16 items-center justify-center rounded-pill bg-surface text-primary">
              <IconUser size={30} stroke={2} aria-hidden />
            </div>
            <h1 className="font-display mt-4 text-center text-2xl text-content">{t('aboutYou')}</h1>
            <p className="mt-1 text-center text-base text-muted">{t('aboutYouSub')}</p>
          </div>

          <div className="mt-8 px-6">
            <label className="mb-1.5 block text-sm font-bold tracking-wide text-muted">
              {t('yourNameLabel')} <span className="text-error">*</span>
            </label>
            <input
              {...register('parentName')}
              type="text"
              autoFocus
              placeholder={t('namePlaceholder')}
              className="h-12 w-full rounded-md border-[1.5px] border-border bg-surface px-5 text-base text-content placeholder-hint outline-none transition-colors focus:border-primary"
            />
            {errors.parentName && (
              <p className="mt-1 text-sm text-error">{errors.parentName.message}</p>
            )}
          </div>

          {/* DESIGN.md's segmented-control recipe: a `bg-bg` rounded-md track
              holding one min-h-11 button per option, the selected one filled. */}
          {!onInvitePath && (
            <div className="mt-6 px-6">
              <label className="mb-1.5 block text-sm font-bold tracking-wide text-muted">
                {t('appLanguageLabel')}
              </label>
              <div className="flex w-full rounded-md bg-bg p-0.5">
                {LANGUAGES.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => {
                      // Only this explicit tap counts as `language_switched` — the
                      // device-locale auto-seed above (seedFromDevice, mount time)
                      // is not a parent action and must not be counted as one.
                      capture('language_switched', { language: code, app: 'parent' });
                      setLanguage(code);
                    }}
                    aria-pressed={language === code}
                    className={`min-h-11 flex-1 cursor-pointer rounded-md text-sm font-bold transition-all ${
                      language === code ? 'bg-primary text-surface shadow-sm' : 'text-muted'
                    }`}
                  >
                    {LANGUAGE_LABELS[code]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {errors.root && (
            <p className="mt-3 px-6 text-center text-sm text-error">{errors.root.message}</p>
          )}

          <div className="mt-auto px-6 pt-6 md:mt-0">
            <button
              type="submit"
              disabled={!nameEntered || isSubmitting}
              className="cta-primary disabled:opacity-40"
            >
              {isSubmitting ? t('saving') : t('continueLabel')}
            </button>
            <p className="mt-3 text-center text-sm text-hint">
              {onInvitePath ? t('changeNameLaterNote') : t('changeLaterNote')}
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
